import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Observable, of } from 'rxjs';
import { DiagnosisService } from 'src/app/services/diagnosis.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { map } from 'rxjs/operators';
import { NotificationsService } from 'src/app/services/notifications.service'
import { valueChangesPipe } from 'src/app/utils/value-changes-pipe.function';
import { Cie10 } from 'src/app/models/cie10.model';
import { normalize } from 'src/app/utils/sanitizer.function';
import { errorStateMatcher } from 'src/app/utils/error-state-matcher.function';
import * as _ from 'lodash';
import { MatSelectChange } from '@angular/material/select';
import { ErrorStateMatcher } from '@angular/material/core';
import { Call } from 'src/app/models/call.model';
import { CALL_RESOLUTIONS } from 'src/app/consts/call-resolutions.const';
import { LocaleService } from 'src/app/services/locale.service';
import { CallResolutions } from 'src/app/enums/call-resolutions.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogData } from 'src/app/models/dialog-data.model';
import { autocompleteObjectValidator } from 'src/app/utils/autocomplete-validators.function';
import { Diagnosis } from 'src/app/models/diagnosis.model';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';

@Component({
  selector: 'app-diagnosis',
  templateUrl: './diagnosis.component.html',
  styleUrls: ['./diagnosis.component.css'],
  providers: [
    { provide: ErrorStateMatcher, useClass: errorStateMatcher },
  ],
  standalone: false
})
export class DiagnosisComponent implements OnInit {
  @Input('call') call!: Call;

  @Input() medicalHistory?: string;
  @Input() canPrescribeMedication!: boolean;

  @Output()
  diagnosisDuplicatedEvent = new EventEmitter<boolean>();

  resolutionOptions = CALL_RESOLUTIONS;

  failedResolutions = [
    CallResolutions.CONNECTION_FAILED,
    CallResolutions.COULD_NOT_CONTACT,
    CallResolutions.WRONG_PHONE,
    CallResolutions.CONSULTATION_PREVIOUSLY_SOLVED
  ];

  validationType = {
    motive: [Validators.required],
    diagnosis: [Validators.required, autocompleteObjectValidator()],
    text: [Validators.required],
    resolution: [Validators.required],
    treatment: [Validators.required],
    patientRecords: [Validators.required]
  };

  formGroup!: FormGroup;
  diagnosisList: Cie10[] = [];
  filteredDiagnosisList!: Observable<Cie10[]>;

  readonly treatmentMaxLength = 250;

  constructor(
    private matDialog: MatDialog,
    private diagnosisService: DiagnosisService,
    private notificationsService:NotificationsService,
    private sharedDataService: SharedDataService,
    private localeService: LocaleService,
    private fb: FormBuilder,
  ) { }

  async ngOnInit(): Promise<void> {
    this.buildForm();
    await this.getCie10();

    this.resolutionOptions = this.fillResolutions(this.call)
    this.subscribeToFormChanges();
    this.patchFormValues();
  }

  buildForm(): void {
    this.formGroup = this.fb.group({
      diagnosis: new FormControl('', this.validationType.diagnosis),
      text: new FormControl('', this.validationType.text),
      resolution: new FormControl(null, this.validationType.resolution),
      problemPatient: new FormControl(false),
      notes: new FormControl(''),
      motive: new FormControl('', this.validationType.motive),
      patientRecords: new FormControl('', this.validationType.patientRecords),
      treatment: new FormControl('', this.validationType.treatment),
      requiresPrescription: new FormControl({ value: false, disabled: this.canPrescribeMedication && !this.call.provider.isRedGlobal }),
    });
  }

  patchFormValues(): void {
    this.formGroup.patchValue({
      patientRecords: this.medicalHistory ?? '',
      motive: this.call.motive || '',
    })
  }

  async continue(): Promise<void> {
    const selectedResolution = this.resolution.value;
    const notes = this.notes.value?.trim();
    const newDiagnosis = {
      call: this.call.id,
      diagnosis: this.diagnosis.value?.code,
      solved: selectedResolution.value === CallResolutions.SOLVED,
      urgent: selectedResolution.value === CallResolutions.URGENT,
      referred: selectedResolution.value === CallResolutions.REFERRED,
      connectionFailed: selectedResolution.value === CallResolutions.CONNECTION_FAILED,
      patientDischarged: selectedResolution.value === CallResolutions.PATIENT_DISCHARGED,
      couldNotContact: selectedResolution.value === CallResolutions.COULD_NOT_CONTACT,
      wrongPhone: selectedResolution.value === CallResolutions.WRONG_PHONE,
      consultationPreviouslySolved: selectedResolution.value === CallResolutions.CONSULTATION_PREVIOUSLY_SOLVED,
      discontinueMonitoring: selectedResolution.value === CallResolutions.DISCONTINUE_MONITORING,
      text: this.text.value,
      problemPatient: this.problemPatient.value,
      motive: this.motive.value,
      patientRecords: this.patientRecords.value,
      treatment: this.treatment.value,
      requiresPrescription: this.requiresPrescription.value,
      ...(notes && {notes})
    }

    const continueSubmit = await this.showDialogIfNonBillableConnectionFailed(newDiagnosis);
    if (!continueSubmit) return;

    this.diagnosisService.create(newDiagnosis).then((diagnosis) => {
      this.call.diagnosis = diagnosis;
      this.sharedDataService.diagnosisComplete = true;
    }).catch((err) => {
      const duplicatedDiagnosis = err.error?.name === "diagnosisAlreadyExists";
      const data = this.handleDiagnosisError(duplicatedDiagnosis, err);
      this.notificationsService.openErrorDialog(data, duplicatedDiagnosis);
    })
  }

  private handleDiagnosisError(duplicatedDiagnosis: boolean, err: HttpErrorResponse): Partial<DialogData> {
    let data: Partial<DialogData>;
    if(duplicatedDiagnosis) {
      this.diagnosisDuplicatedEvent.emit(true);
      data = {
        message: err.error.message,
        title: "default_warning_message",
        actions: [{
          function: () => undefined,
          message: "continue"
        }]
      }
    } else {
      const message = err.status === 401 ? err?.error?.message : null;
      data = {
        message: message || "diagnosis_error",
        error: err.status !== 401 ? err.error || err : null,
        title: "default_warning_message",
        actions: [{
          function: () => undefined,
          message: "default_accept"
        }]
      }
    }
    return data;
  }

  checkOptionalFields(event: MatSelectChange): void {
    const resolution = _.find(this.resolutionOptions, resolution => resolution === event.value);
    if(this.failedResolutions.includes(resolution?.value as CallResolutions)) {
      this.clearFieldsValidators();
      this.updateFormValueAndValidity();
    } else {
      this.setRequiredValidatorToFields();
      if(this.requiresPrescription?.value) this.addTreatmentMaxLengthValidator();
      this.updateFormValueAndValidity();
    }
  }

  addTreatmentMaxLengthValidator(): void {
    this.treatment.addValidators(Validators.maxLength(this.treatmentMaxLength));
    if(this.treatment.value?.length > 0) this.treatment.markAllAsTouched();
  }

  fillResolutions(call: Call) {
    let resolutions = CALL_RESOLUTIONS

    if(!call.provider.supportedMonitorings?.length) {
      resolutions = resolutions.filter((resolution) => {
        return resolution.value != CallResolutions.DISCONTINUE_MONITORING &&
          resolution.value != CallResolutions.PATIENT_DISCHARGED
      })
    }

    return resolutions
  }

  async getCie10(): Promise<void> {
    this.diagnosisList = await this.diagnosisService.getCie10();
    this.filteredDiagnosisList = of(this.diagnosisList);
  }

  updateFormValueAndValidity(): void {
    this.text.updateValueAndValidity();
    this.motive.updateValueAndValidity();
    this.patientRecords.updateValueAndValidity();
    this.treatment.updateValueAndValidity();
    this.diagnosis.updateValueAndValidity();
  }

  clearFieldsValidators(): void {
    this.text.clearValidators();
    this.motive.clearValidators();
    this.patientRecords.clearValidators();
    this.treatment.clearValidators();
    this.diagnosis.clearValidators();
  }

  setRequiredValidatorToFields(): void {
    this.text.setValidators(this.validationType.text);
    this.motive.setValidators(this.validationType.motive);
    this.patientRecords.setValidators(this.validationType.patientRecords);
    this.treatment.setValidators(this.validationType.treatment);
    this.diagnosis.setValidators(this.validationType.diagnosis);
  }

  private subscribeToFormChanges(): void {
    this.filteredDiagnosisList = valueChangesPipe(this.diagnosis.valueChanges)
    .pipe(map((value: string) => this.filterItemsAutocomplete(this.diagnosisList, value)));

    this.requiresPrescription.valueChanges.subscribe((requiresPrescription) => {
      if(requiresPrescription) this.addTreatmentMaxLengthValidator();
      else this.treatment.setValidators(Validators.required)
    })
  }

  private filterItemsAutocomplete(items: Cie10[], valueToSearch: string): Cie10[] {
    valueToSearch = normalize(valueToSearch.toLowerCase().trim());
    return items.filter((item: Cie10) =>
      normalize(item.searchValue?.toLowerCase()).includes(valueToSearch)
    );
  }

  private diagnosisHasBeenCompleted = function (diagnosis: Partial<Diagnosis>) {
    if (typeof diagnosis !== 'object') return false;
    return diagnosis.text?.length && diagnosis.motive?.length && diagnosis.diagnosis?.length && diagnosis.treatment?.length;
  };

  async showDialogIfNonBillableConnectionFailed(diagnosis: Partial<Diagnosis>): Promise<boolean> {
    const showWarningDialog = diagnosis?.connectionFailed && !this.diagnosisHasBeenCompleted(diagnosis);
    if (!showWarningDialog) return true;

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: "352px",
      panelClass: 'bg-dark',
      data: {
        title: "dialog_title_warning",
        message: "diagnosis_connection_failed_non_billable",
        canCancel: true,
        closeMessage: 'cancel',
        actions: [
          { 
            message: 'default_accept'
          }
        ]
      }
    })
    const result = await firstValueFrom(dialogRef.afterClosed());
    return !!result;
  }

  displayDiagnosisFn(diagnosis: {searchValue: string}): string {
    return diagnosis?.searchValue || '';
  }

  displayResolutionFn = (resolution?: {value: string, name: string}): string => {
    return resolution ? this.localeService.getString(resolution.name) : '';
  }

  get diagnosis(): FormControl<Cie10> { return this.formGroup.get('diagnosis') as FormControl }
  get resolution(): FormControl<{value: string, name: string}> { return this.formGroup.get('resolution') as FormControl }
  get motive(): FormControl<string> { return this.formGroup.get('motive') as FormControl }
  get patientRecords(): FormControl<string> { return this.formGroup.get('patientRecords') as FormControl }
  get text(): FormControl { return this.formGroup.get('text') as FormControl }
  get treatment(): FormControl<string> { return this.formGroup.get('treatment') as FormControl }
  get notes(): FormControl<string> { return this.formGroup.get('notes') as FormControl }
  get problemPatient(): FormControl<boolean> { return this.formGroup.get('problemPatient') as FormControl }
  get requiresPrescription(): FormControl<boolean> { return this.formGroup.get('requiresPrescription') as FormControl }
}
